import { useState } from "react";
import useScrollReveal from "./shared/useScrollReveal";

const TABS = [
  {
    label: "Power & Electrical",
    previewTitle: "Power & Electrical",
    previewSubtitle: "Input supply, output & electrical performance",
    icon: <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />,
    rows: [
      { key: "Input Voltage", a: "415 VAC (\u00b110%), 3-phase, 50 Hz", b: "415 VAC (\u00b110%), 3-phase, 50 Hz" },
      { key: "Wires", a: "Three Phase (L1, L2, L3, N, PE)", b: "Three Phase (L1, L2, L3, N, PE)" },
      { key: "Output Interface", a: "Gun1: Type 6, Gun2: Type 7", b: "Gun1: Type 6/Type 7" },
      { key: "Output Power", a: "12 kW (6 + 6 kW split)", b: "12 kW" },
      { key: "Output Current", a: "200A (100A per gun \u00d7 2)", b: "125A" },
      { key: "Efficiency", a: "96% at nominal output power", b: "96% at nominal output power" },
      { key: "Power Factor", a: "0.99 at nominal output power", b: "0.99 at nominal output power" },
      { key: "Current THD", a: "5% at nominal output power", b: "5% at nominal output power" },
    ],
  },
  {
    label: "Connectivity",
    previewTitle: "Connectivity & Communication",
    previewSubtitle: "Network, protocols & standards",
    icon: <path d="M5 12.55a11 11 0 0 1 14.08 0M1.42 9a16 16 0 0 1 21.16 0M8.53 16.11a6 6 0 0 1 6.95 0M12 20h.01" />,
    rows: [
      { key: "Network", a: "GSM/3G/4G, Ethernet, Wi-Fi, Bluetooth", b: "GSM/3G/4G, Ethernet, Wi-Fi, Bluetooth" },
      { key: "Protocol", a: "OCPP 1.6J", aTag: "Upgradable to 2.1", b: "OCPP 1.6J", bTag: "Upgradable to 2.1" },
      { key: "Vehicle Standards", a: "IEC 61851, ISO 11898-1, ISO 11898-2", b: "IEC 61851, ISO 11898-1, ISO 11898-2" },
    ],
  },
  {
    label: "Mechanical & Env.",
    previewTitle: "Mechanical & Environment",
    previewSubtitle: "Physical build, operating conditions & certification",
    icon: <><path d="M21 6H3M21 12H3M21 18H3" /><path d="M16 3v6M8 3v6" /></>,
    rows: [
      { key: "Dimensions (L\u00d7W\u00d7H)", a: "525 \u00d7 270 \u00d7 700 mm", b: "1720 \u00d7 540 \u00d7 275 mm" },
      { key: "Weight", a: "60 kg", b: "130 kg" },
      { key: "IP Rating", a: "IP54", aTag: "Weatherproof", b: "IP54", bTag: "Weatherproof" },
      { key: "Cooling", a: "Air Cooling", b: "Air Cooling" },
      { key: "Cable Length", a: "15 ft", b: "15 ft" },
      { key: "Operating Temperature", a: "-10\u00b0C to +55\u00b0C (derating applies)", b: "-10\u00b0C to +55\u00b0C (derating applies)" },
      { key: "Storage Temperature", a: "-10\u00b0C to +70\u00b0C (dry environment)", b: "-10\u00b0C to +70\u00b0C (dry environment)" },
      { key: "Altitude", a: "2000 m", b: "2000 m" },
      { key: "Humidity", a: "5%\u201395%, non-condensing", b: "5%\u201395%, non-condensing" },
      { key: "Noise Level", a: "\u2264 55 dB", b: "\u2264 55 dB" },
      { key: "Certification", a: "ARAI", aTag: "In Progress", b: "ARAI", bTag: "In Progress" },
    ],
  },
  {
    label: "UI & Safety",
    previewTitle: "User Interface & Safety",
    previewSubtitle: "Display, controls & protection systems",
    icon: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="M9 12l2 2 4-4" /></>,
    rows: [
      { key: "Display", a: "4.3\" Dwin LCD Touch Screen", b: "10.4\" TFT LCD Touch Screen" },
      { key: "Visual Indication", a: "RGB Neo Pixel LEDs", b: "RGB Neo Pixel LEDs" },
      { key: "Emergency Stop", a: "Mushroom Type", b: "Mushroom Type" },
      { key: "User Authentication", a: "App / QR / RFID / Password / Optional Autocharge / Optional UPI", b: "App / QR / RFID / Password / Optional Autocharge / Optional UPI", smallText: true },
      { key: "RFID Specs", a: "RC522, ISO/IEC 14443A, MIFARE", b: "RC522, ISO/IEC 14443A, MIFARE" },
      {
        key: "Protection Features",
        a: "Over-Current, Over-Voltage, Over-Temperature, Under-Voltage, Surge, Short Circuit, Ground Fault, Emergency Shutdown, Polarity Reverse, Insulation Monitoring, Leakage, Earth Detection",
        b: "Over-Current, Over-Voltage, Over-Temperature, Under-Voltage, Surge, Short Circuit, Ground Fault, Emergency Shutdown, Polarity Reverse, Insulation Monitoring, Leakage, Cable Temp Detection, Earth Detection",
        smallText: true,
      },
    ],
  },
];

export default function Specs() {
  const ref = useScrollReveal();
  const [activeTab, setActiveTab] = useState(0);
  const tab = TABS[activeTab];

  return (
    <section className="specs" id="specs" ref={ref}>
      <div className="container">
        <p className="section-label reveal text-center">Technical Specifications</p>
        <h2 className="section-title reveal text-center md:pb-6">Every Number that Matters</h2>

        <div className="sw">
          <div className="specs-layout">
            <div className="specs-sidebar">
              {TABS.map((t, i) => (
                <div key={t.label}>
                  <button className={`tab-btn ${i === activeTab ? "active" : ""}`} onClick={() => setActiveTab(i)}>
                    <div className="tab-icon">
                      <svg viewBox="0 0 24 24">{t.icon}</svg>
                    </div>
                    <div className="tab-meta"><span className="tab-label">{t.label}</span></div>
                  </button>
                  {i < TABS.length - 1 && <div className="tab-divider" />}
                </div>
              ))}
            </div>

            <div className="specs-preview">
              <div className="panel">
                <div className="preview-header">
                  <div className="preview-header-left">
                    <div className="preview-header-icon"><svg viewBox="0 0 24 24">{tab.icon}</svg></div>
                    <div>
                      <div className="preview-title">{tab.previewTitle}</div>
                      <div className="preview-subtitle">{tab.previewSubtitle}</div>
                    </div>
                  </div>
                </div>
                <table className="spec-table">
                  <colgroup><col style={{ width: "34%" }} /><col style={{ width: "33%" }} /><col style={{ width: "33%" }} /></colgroup>
                  <thead>
                    <tr>
                      <th>Parameter</th>
                      <th className="col-val col-a">12 kW Dual Gun</th>
                      <th className="col-val col-b">12 kW Single Gun</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tab.rows.map((row) => {
                      const isSame = row.a === row.b && row.aTag === row.bTag;
                      return (
                        <tr key={row.key}>
                          <td className="spec-key">{row.key}</td>
                          {isSame ? (
                            <td
                              className="spec-val spec-val-merged"
                              colSpan={2}
                              style={row.smallText ? { fontWeight: 600, fontSize: "16px", lineHeight: 1.6 } : undefined}
                            >
                              {row.a}{row.aTag && <span className="tag">{row.aTag}</span>}
                            </td>
                          ) : (
                            <>
                              <td
                                className="spec-val col-a"
                                data-variant="6 kW"
                                style={row.smallText ? { fontWeight: 500, fontSize: "14px", lineHeight: 1.6 } : undefined}
                              >
                                {row.a}{row.aTag && <span className="tag">{row.aTag}</span>}
                              </td>
                              <td
                                className="spec-val col-b"
                                data-variant="12 kW"
                                style={row.smallText ? { fontWeight: 500, fontSize: "14px", lineHeight: 1.6 } : undefined}
                              >
                                {row.b}{row.bTag && <span className="tag">{row.bTag}</span>}
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}